import { SelectChangeEvent, MenuItem, Box } from '@mui/material';
import SweepSelect from '../SweepSelect';
import usePermission from '../permissions/usePermission';
import { Typography, colors } from '@sweep-io/sweep-design';
import { UserDefinedRoleGroupsInterface } from '@server/role-group-interface';
import { sortRoles } from '../../dashboard/role-manager/utils';
import { useState } from 'react';
import { useSweepRoleGroups } from '../useSweepRoleGroups';
import { ClampedText } from '../ClampedText';

interface RolesDropdownProps {
  userId?: string;
  userRole?: UserDefinedRoleGroupsInterface;
  accountRoles: UserDefinedRoleGroupsInterface[];
  onChangeRoleCb: (userId: string, roleId: string) => void;
  onUserRemove?: (userId: string) => void;
  disableRoleDropdown?: boolean;
}

export const RolesDropdown = ({
  userId,
  userRole,
  accountRoles,
  onChangeRoleCb,
  disableRoleDropdown,
  onUserRemove,
}: RolesDropdownProps) => {
  const { getUserRole } = useSweepRoleGroups();
  const [isAllowedBtn, isAllowedBtnDelete] = usePermission(['edit:users:roles', 'delete:users']);
  const [_userRole, setUserRole] = useState(userRole);

  if (disableRoleDropdown || !isAllowedBtn) {
    return (
      <Box sx={{ paddingRight: 1, mt: '10px' }}>
        <Typography ellipsis variant="body">
          {userRole?.name}
        </Typography>
      </Box>
    );
  }

  const sorted = sortRoles(accountRoles);

  return (
    <SweepSelect
      removeBorders
      SelectProps={{
        renderValue: (value) => (
          <Typography variant="body">{getUserRole(value as string)?.name}</Typography>
        ),
        value: _userRole?.id ?? '',
        onChange: (event: SelectChangeEvent<unknown>) => {
          event.preventDefault();
          const roleId = event.target.value as string;

          if (roleId === 'remove_user' && userId) {
            onUserRemove && onUserRemove(userId);
            return;
          }

          const role = accountRoles.find((role) => role.id === roleId);
          onChangeRoleCb(userId || '', roleId);
          role && setUserRole(role);
        },
        MenuProps: {
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'right',
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'right',
          },
        },
        sx: {
          maxHeight: '440px',
          overflow: 'hidden',
          '& .MuiSelect-select': {
            paddingRight: 3,
          },
        },
      }}
    >
      {sorted.map((role) => (
        <MenuItem
          key={role.id}
          value={role.id}
          selected={_userRole?.id === role.id}
          sx={{
            p: 1,
            mb: 0.5,
            width: '312px',
            '&.Mui-selected': {
              backgroundColor: colors.grey[100],
              ' .MuiTypography-body-medium': {
                color: colors.blue[500],
              },
              '&:hover': {
                backgroundColor: colors.grey[100],
              },
              '&:after': {
                backgroundImage: `url('data:image/svg+xml;utf8,<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M13.5 4.5L6.5 11.5L3 8" stroke="${encodeURIComponent(
                  colors.blue[500],
                )}" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path></svg>')`,
              },
            },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '4px',
              minWidth: 0,
            }}
          >
            <Typography variant={_userRole?.id === role.id ? 'body-medium' : 'body'}>
              {role.name}
            </Typography>
            <ClampedText
              text={role.description}
              typographyVariant="caption"
              textColor={colors.grey[700]}
            />
          </Box>
        </MenuItem>
      ))}

      {isAllowedBtnDelete && onUserRemove && userId && (
        <MenuItem
          value="remove_user"
          sx={{ background: colors.white, color: colors.blush[500], p: 1 }}
        >
          Remove
        </MenuItem>
      )}
    </SweepSelect>
  );
};
